import {CSSStyles} from '../../types/CSSStyles';
import useAppTheme from '../design_system/hooks/useAppTheme';
import useMediaQuery from '../design_system/hooks/useMediaQuery';
import {MediaQuery} from '../design_system/hooks/useMediaQueryGetter';

import {css} from '@emotion/react';
import React from 'react';

export interface Props {
  readonly children: React.ReactNode;
  /**
   * @default true
   */
  readonly hasContentPadding?: boolean;
  /**
   * @default true
   */
  readonly hasPagePadding?: boolean;
  readonly rootStyle?: CSSStyles;
  readonly style?: CSSStyles;
}

const FULL_SIZE = css({
  boxSizing: 'border-box',
  width: '100%',
  height: '100%',
});

/**
 * PageContainer component
 *
 * @description A layout container that provides consistent padding, width constraints, and responsive behavior for page content.
 * It supports customizable paddings for both the page and its content, and adjusts styles based on screen size.
 *
 * @prop {React.ReactNode} children - The content to render inside the container.
 * @prop {boolean} [hasContentPadding=true] - Determines whether the content inside the container has side paddings.
 * @prop {boolean} [hasPagePadding=true] - Determines whether the outer page container has side paddings.
 * @prop {CSSStyles} [rootStyle] - Additional custom styles applied to the root content wrapper.
 * @prop {CSSStyles} [style] - Additional custom styles applied to the outermost container.
 *
 * @default
 * - hasContentPadding: true
 * - hasPagePadding: true
 * - paddingBottom: spacing.x80
 *
 * @responsive
 * - Page paddings (`paddingLeft` and `paddingRight`) adjust based on screen size:
 *   - Small screens: `sizes.sm.pageSidePadding`
 *   - Medium screens: `sizes.md.pageSidePadding`
 *   - Large screens: `sizes.l.pageSidePadding`
 * - Content paddings (`paddingLeft` and `paddingRight`) adjust based on screen size:
 *   - Small screens: `sizes.sm.pageContentSidePadding`
 *   - Medium screens: `sizes.md.pageContentSidePadding`
 *   - Large screens: `sizes.l.pageContentSidePadding`
 *
 * @example
 * <PageContainer hasContentPadding={false} hasPagePadding={true} style={{ backgroundColor: 'lightgray' }}>
 *   <h1>Hello, World!</h1>
 * </PageContainer>
 */
export default function PageContainer(props: Props): JSX.Element {
  const {
    children,
    hasContentPadding = true,
    hasPagePadding = true,
    rootStyle,
    style,
  } = props;
  const {sizes, spacing} = useAppTheme();

  const pageCss = [
    css({
      boxSizing: 'border-box',
      paddingLeft: sizes.l.pageSidePadding,
      paddingRight: sizes.l.pageSidePadding,

      [useMediaQuery(MediaQuery.SmallScreen)]: {
        paddingLeft: sizes.sm.pageSidePadding,
        paddingRight: sizes.sm.pageSidePadding,
      },
      [useMediaQuery(MediaQuery.MediumScreen)]: {
        paddingLeft: sizes.md.pageSidePadding,
        paddingRight: sizes.md.pageSidePadding,
      },
    }),
    FULL_SIZE,
  ];

  const contentCss = [
    css({
      paddingLeft: sizes.l.pageContentSidePadding,
      paddingRight: sizes.l.pageContentSidePadding,

      [useMediaQuery(MediaQuery.SmallScreen)]: {
        paddingLeft: sizes.sm.pageContentSidePadding,
        paddingRight: sizes.sm.pageContentSidePadding,
      },
      [useMediaQuery(MediaQuery.MediumScreen)]: {
        paddingLeft: sizes.md.pageContentSidePadding,
        paddingRight: sizes.md.pageContentSidePadding,
      },
    }),
    FULL_SIZE,
  ];

  return (
    <div
      css={[
        css({
          paddingBottom: spacing.x80,
        }),
        FULL_SIZE,
        style,
      ]}
    >
      <div css={hasPagePadding && pageCss}>
        <div
          css={[
            hasContentPadding && contentCss,
            css({maxWidth: sizes.maxWidth, margin: '0 auto'}),
          ]}
        >
          <div css={rootStyle}>{children}</div>
        </div>
      </div>
    </div>
  );
}
